import type { ReactNode } from 'react';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface DropdownProps {
  trigger: ReactNode;
  content: ReactNode;
  dropdownWidth: number;
  dropdownHeight: number;
  offsetX?: number;
  offsetY?: number;
  onChange?: (isOpen: boolean) => void;
}

const Dropdown = forwardRef((props: DropdownProps, ref) => {
  const { trigger, content, dropdownWidth, dropdownHeight, offsetX = 0, offsetY = 0, onChange } = props;
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const updateDropdownPosition = () => {
    if (triggerRef.current && dropdownRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();

      const viewportHeight = window.innerHeight;

      if (viewportHeight - rect.bottom < dropdownHeight) {
        dropdownRef.current.style.top = `${rect.top - dropdownHeight - offsetY}px`;
      } else {
        dropdownRef.current.style.top = `${rect.bottom + offsetY}px`;
      }
      dropdownRef.current.style.left = `${rect.left}px`;
    }
  };

  useEffect(() => {
    if (isOpen) {
      updateDropdownPosition();
      window.addEventListener('scroll', updateDropdownPosition, true);
      window.addEventListener('resize', updateDropdownPosition);
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      window.removeEventListener('scroll', updateDropdownPosition, true);
      window.removeEventListener('resize', updateDropdownPosition);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, offsetX]);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
    onChange && onChange(!isOpen);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node) &&
      triggerRef.current &&
      !triggerRef.current.contains(event.target as Node)
    ) {
      toggleDropdown();
    }
  };

  useImperativeHandle(ref, () => ({
    toggleDropdown,
  }));

  return (
    <>
      <div ref={triggerRef} onClick={toggleDropdown} className='aaaaaaa'>
        {trigger}
      </div>
      {isOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            style={{
              transform: 'translateX(-11px)',
              width: `${dropdownWidth}px`,
              height: `${dropdownHeight}px`,
              position: 'fixed',
              opacity: 1,
              zIndex: 99999999999999000,
            }}
          >
            {content}
          </div>,
          document.body
        )}
    </>
  );
});

export default Dropdown;
